'use client'

import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useViewport } from '@/components/findex-connect/hooks/useViewport'
import { useThemeStore } from '@/components/findex-connect/store/ThemeStore'

import { CrossIcon } from '../Icons/CrossIcon/CrossIcon'

export const HubspotPanel: React.FC = () => {
  const [isMobileCtaVisible, setIsMobileCtaVisible] = useState(false)
  const activeSection = useThemeStore((state) => state.activeSection)
  const isMobileFormVisible = useThemeStore((state) => state.isMobileFormVisible)
  const { viewportWidth } = useViewport()

  // The desktop container is only shown from the connect lg breakpoint (64rem,
  // tailwind.connect.config.js). CSS display:none does not stop the embed from
  // mounting, so without this gate phones load the HubSpot script, render a
  // full hidden copy of the form and fetch its header image on page load, then
  // mount a second copy of everything when the popup opens.
  const isDesktopViewport = viewportWidth >= 1024

  useEffect(() => {
    if (
      (!activeSection || (activeSection !== 'overview' && activeSection !== 'get-started')) &&
      !isMobileFormVisible
    ) {
      setIsMobileCtaVisible(true)
    } else {
      setIsMobileCtaVisible(false)
    }
  }, [activeSection, isMobileFormVisible])

  // LOCK SCROLL WHEN FORM OPEN
  useEffect(() => {
    if (isMobileFormVisible) {
      document.body.classList.add('overflow-y-hidden')
    } else {
      document.body.classList.remove('overflow-y-hidden')
    }
  }, [isMobileFormVisible])

  const HubspotFormEmbed: React.FC<{ className?: string; keyString: string }> = ({ className }) => {
    const formWrapperRef = useRef<HTMLDivElement>(null)
    const PORTAL_ID = '441838444'
    const REGION = 'ap1'

    useEffect(() => {
      const script = document.createElement('script')
      const scriptSrc = `https://js-${REGION}.hsforms.net/forms/embed/developer/${PORTAL_ID}.js`

      script.src = scriptSrc
      script.defer = true

      document.body.appendChild(script)

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script)
        }
      }
    }, [])

    // Handle form ready event
    useEffect(() => {
      const handleFormReady = (event: Event) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const form = (window as any).HubSpotFormsV4?.getFormFromEvent?.(event)
        if (form) {
          console.warn('🟢 HubSpot form ready')
        }
      }

      window.addEventListener('hs-form-event:on-ready', handleFormReady)
      return () => window.removeEventListener('hs-form-event:on-ready', handleFormReady)
    }, [])

    // SUBMISSION INTERCEPTION
    useEffect(() => {
      const formElement = formWrapperRef.current
      if (!formElement) return

      const observer = new MutationObserver((mutationsList, observer) => {
        const form = formElement.querySelector('form')
        if (form) {
          // Stop observing once the form is found
          observer.disconnect()

          // Attach our custom submit handler to the form
          const handleSubmit = (event: SubmitEvent) => {
            // Prevent default submission initially to read the data
            event.preventDefault()

            console.warn('--- Custom Submit Intercepted ---')

            // Grab all form data using the standard FormData API
            const formData = new FormData(form)
            const formDataObject: { [key: string]: string } = {}

            formData.forEach((value, key) => {
              if (typeof value === 'string') {
                formDataObject[key] = value
              }
            })

            console.warn('🟡 Form Data Captured (Before Post):', formDataObject)

            // PUSH TO DATALAYER
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
              event: 'hubspotFormSubmitAttempt',
              formId: '9074b57f-20f2-43e7-ad5f-5433dfc4afb6',
              formData: formDataObject,
            })

            setTimeout(() => form.submit(), 100)
          }

          form.addEventListener('submit', handleSubmit)
          return () => form.removeEventListener('submit', handleSubmit)
        }
      })

      observer.observe(formElement, { childList: true, subtree: true })
      return () => observer.disconnect()
    }, [])

    // ON-SUCCESS/ON-SUBMIT (For Confirmation)
    useEffect(() => {
      // We use the success event to confirm the post succeeded
      const handleFormSuccess = (event: Event) => {
        console.warn('🟢 HubSpot form submit confirmed (SUCCESSFUL)', event)

        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'hubspotFormSubmitSuccess',
          formId: '9074b57f-20f2-43e7-ad5f-5433dfc4afb6',
        })
      }

      // Using the official success event
      window.addEventListener('hs-form-event:on-submission:success', handleFormSuccess)

      return () =>
        window.removeEventListener('hs-form-event:on-submission:success', handleFormSuccess)
    }, [])

    return (
      <div
        className={className}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          overflow: 'auto',
          maxHeight: '100vh',
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="https://a-ap.storyblok.com/f/3000455/1253x501/d8a4406605/form_header.jpg"
          alt="Form Header"
          style={{
            width: '100%',
            display: 'block',
          }}
        />
        <div
          ref={formWrapperRef}
          className="hs-form-html"
          data-region={REGION}
          data-form-id="9074b57f-20f2-43e7-ad5f-5433dfc4afb6"
          data-portal-id={PORTAL_ID}
          style={{
            minWidth: '100%',
            maxWidth: '100%',
            minHeight: 'auto',
            border: 'none',
            padding: '20px 20px 35px 20px',
            overflow: 'auto',
          }}
        />
      </div>
    )
  }

  const desktopHubspot = useMemo(
    () => <HubspotFormEmbed className="h-full w-full" keyString="desktop" />,
    []
  )

  const mobileHubspot = useMemo(
    () => <HubspotFormEmbed className="h-full w-full" keyString="mobile" />,
    []
  )

  return (
    <>
      <div className="HUBSPOT-CONTAINER-DESKTOP clip-path-rounded-xl relative hidden lg:flex lg:w-[500px] 2xl:w-[532px]">
        {isDesktopViewport && desktopHubspot}
      </div>

      {/** MOBILE */}
      <div
        className={
          'HUBSPOT-CONTAINER-MOBILE absolute bottom-0 left-0 z-50 w-full lg:hidden ' +
          (isMobileFormVisible ? 'h-full' : 'h-[90px]')
        }
      >
        <AnimatePresence>
          {isMobileFormVisible && (
            <motion.div
              className="HUBSPOT-MOBILE-WRAPPER fixed top-0 bottom-0 left-0 z-50 h-svh w-screen overflow-hidden border-0 md:top-18"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="HUBSPOT-TAP-OUTSIDE-WRAPPER fixed top-0 bottom-0 left-0 h-svh w-screen overflow-hidden border-0 bg-black/50"
                onClick={() =>
                  useThemeStore.setState({
                    isMobileFormVisible: false,
                  })
                }
              />
              <motion.div
                className="HUBSPOT-INNER absolute inset-0"
                initial={{ opacity: 0, y: 400 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 400 }}
                transition={{
                  duration: 0.7,
                  ease: 'easeInOut',
                }}
              >
                {mobileHubspot}
                <div
                  className="MOBILE-CLOSE-BUTTON text-prominent absolute top-6 right-6 flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm"
                  onClick={() =>
                    useThemeStore.setState({
                      isMobileFormVisible: false,
                    })
                  }
                >
                  Close <CrossIcon />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <div
          className={
            `HUBSPOT-MOBILE-BUTTON-CONTAINER fixed flex h-[90px] w-full items-center justify-center bg-[#E8E9E9] px-3 pt-2 pb-6 transition-all duration-[1.5s] ease-in-out ` +
            (isMobileCtaVisible && !isMobileFormVisible ? ` bottom-0` : ` bottom-[-100px]`)
          }
        >
          <button
            className="bg-primary-dark hover:bg-primary-hover flex w-full cursor-pointer content-center items-center justify-center rounded-full px-6 py-3 font-bold text-white"
            onClick={() => {
              setIsMobileCtaVisible(false)
              useThemeStore.setState({
                isMobileFormVisible: true,
              })
            }}
          >
            Connect your client
          </button>
        </div>
      </div>
    </>
  )
}
